/**
 * "Remember on this device" (spec §20): optional, OFF by default, localStorage
 * only — the data never leaves the browser, exactly like everything else.
 * Enabling it simply starts writing the plan here; disabling deletes the copy.
 */
import type { Plan } from '../model/types';
import type { Scenario } from '../scenarios/scenario';
import type { WizardAnswers } from '../wizard/apply';
import { planFromFileJson, planToFileJson, type PlanBundle } from './encode';
import { CONSTANTS_AS_OF } from '../engine/data/federal';

const KEY = 'retirement-planner.saved-plan.v1';
const DRAFT_KEY = 'retirement-planner.wizard-draft.v1';

/** Whether a remembered plan exists (doubles as the "enabled" flag). */
export function hasLocalPlan(): boolean {
  try {
    return localStorage.getItem(KEY) != null;
  } catch {
    return false; // storage blocked (private mode etc.) — feature just absent
  }
}

export function saveLocalPlan(plan: Plan, scenarios: Scenario[]): void {
  try {
    localStorage.setItem(KEY, planToFileJson(plan, CONSTANTS_AS_OF, scenarios));
  } catch {
    // Quota or blocked storage: fail silently — saving is a convenience.
  }
}

/** Load the remembered plan, running the normal migration chain. */
export function loadLocalPlan(): PlanBundle | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    return planFromFileJson(raw);
  } catch {
    return null; // corrupt or incompatible: treat as absent, never crash
  }
}

export function clearLocalPlan(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // nothing to do
  }
}

// ── Wizard draft: mid-setup answers, so a break resumes the wizard itself ──

export interface WizardDraft {
  tier: 'simple' | 'advanced';
  answers: WizardAnswers;
  gates: string[]; // advanced-tier module gates that were checked
  stepIndex: number;
}

export function saveWizardDraft(draft: WizardDraft): void {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // Quota or blocked storage: fail silently — saving is a convenience.
  }
}

export function loadWizardDraft(): WizardDraft | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw) as WizardDraft;
    if ((d.tier !== 'simple' && d.tier !== 'advanced') || typeof d.answers?.you !== 'object') {
      return null;
    }
    return {
      tier: d.tier,
      answers: d.answers,
      gates: Array.isArray(d.gates) ? d.gates : [],
      stepIndex: typeof d.stepIndex === 'number' ? d.stepIndex : 0,
    };
  } catch {
    return null; // corrupt or incompatible: treat as absent, never crash
  }
}

export function clearWizardDraft(): void {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch {
    // nothing to do
  }
}
